import { useEffect, useState } from "react";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { api, type LiveTotals } from "../api";
import { useRefreshAll } from "../hooks/useRefreshAll";

// Format bps as a human-readable rate. We bias slightly toward the
// larger unit ("999 Mbps" stays Mbps, "1.05 Gbps" steps up) so the
// card reads cleanly and doesn't oscillate between Mbps and Gbps on
// every poll the way a hard 1e9 boundary would.
function fmtBps(v: number): { value: string; unit: string } {
  if (v >= 1e9) return { value: (v / 1e9).toFixed(2), unit: "Gbps" };
  if (v >= 1e6) return { value: (v / 1e6).toFixed(1), unit: "Mbps" };
  if (v >= 1e3) return { value: (v / 1e3).toFixed(1), unit: "kbps" };
  return { value: String(Math.round(v)), unit: "bps" };
}

function fmtPps(v: number): { value: string; unit: string } {
  if (v >= 1e6) return { value: (v / 1e6).toFixed(2), unit: "Mpps" };
  if (v >= 1e3) return { value: (v / 1e3).toFixed(1), unit: "kpps" };
  return { value: String(Math.round(v)), unit: "pps" };
}

interface CardProps {
  label: string;
  value: number;
  trendPct: number;
  format: (v: number) => { value: string; unit: string };
  accent: string;
}

// One realtime card — big number, unit, trend arrow + % change.
// The trend value comes pre-computed from the backend so the card
// stays purely presentational.
function Card({ label, value, trendPct, format, accent }: CardProps) {
  const f = format(value);
  // Trend stays "stable" inside ±1 % to avoid noisy arrows on a
  // mostly-quiet network. Match the dashboard's dark theme by
  // drawing on a translucent panel bound by the accent stripe.
  const isStable = Math.abs(trendPct) < 1;
  const isUp = trendPct > 0;
  const trendColor = isStable
    ? "#94a3b8" // slate-400 for "→"
    : isUp
    ? "#22c55e" // green-500 — generally "more traffic = network healthy"
    : "#ef4444"; // red-500 for a noticeable drop
  return (
    <div
      style={{
        borderLeft: `3px solid ${accent}`,
        background: "var(--bg-1)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        padding: "10px 14px",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: "var(--muted)",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          marginTop: 2,
        }}
      >
        <span
          style={{
            fontFamily: '"SF Mono", Monaco, Menlo, Consolas, monospace',
            fontSize: 22,
            fontWeight: 700,
            color: "var(--strong)",
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.1,
          }}
        >
          {f.value}
        </span>
        <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500 }}>
          {f.unit}
        </span>
      </div>
      <div
        style={{
          fontSize: 11,
          color: trendColor,
          marginTop: 2,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
        title="Percent change between the recent and older half of the live rolling window"
      >
        {isStable ? (
          <>→ stable</>
        ) : isUp ? (
          <>
            <ArrowUpOutlined /> {trendPct.toFixed(1)}%
          </>
        ) : (
          <>
            <ArrowDownOutlined /> {Math.abs(trendPct).toFixed(1)}%
          </>
        )}
      </div>
    </div>
  );
}

interface Props {
  /** Refresh cadence; default 5 s matches the chart and top widgets. */
  refreshMs?: number;
  /**
   * When set, the cards scope to one sFlow source instead of the whole
   * network. Mirrors the chart's Sensor picker so the headline numbers
   * track whatever the chart is showing.
   */
  sensorId?: number;
  /**
   * Traffic class the cards should sum, mirroring the chart's View picker.
   * "total" (default) reads the network-wide `ip` measurement; "nix"/"ixp"
   * read the geo-classified totals. Composes with sensorId.
   */
  view?: "total" | "nix" | "ixp";
}

/**
 * TotalsBar — the four big realtime cards above the dashboard chart.
 * Reads /api/v1/live/totals every refreshMs (default 5 s) and renders
 * BPS-IN, BPS-OUT, PPS-IN, PPS-OUT. Mirrors Wansight's "Realtime"
 * cards on its Overview page.
 *
 * The component is silent on transient errors — keeps the last known
 * frame so a momentary network blip doesn't blank the cards.
 */
export default function TotalsBar({ refreshMs = 5000, sensorId, view = "total" }: Props) {
  const [data, setData] = useState<LiveTotals | null>(null);
  const refreshAllNonce = useRefreshAll();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // One endpoint serves every scope: network-wide, per-sensor, per
        // geo-class, or any combination. The backend swaps the `ip`
        // measurement for `geo` when a view is set and adds a sensor
        // filter — so the cards always match the chart's current scope,
        // trend arrows included.
        const d = await api.liveTotals({ view, sensor: sensorId });
        if (!cancelled) setData(d);
      } catch {
        /* keep last good frame */
      }
    };
    load();
    if (refreshMs > 0) {
      const id = window.setInterval(load, refreshMs);
      return () => {
        cancelled = true;
        window.clearInterval(id);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [refreshMs, refreshAllNonce, sensorId, view]);

  // Render zeros while the first poll is in flight — same shape as
  // the loaded view so the dashboard layout doesn't shift on first
  // data arrival.
  const d: LiveTotals = data ?? {
    bps_in: 0,
    bps_out: 0,
    pps_in: 0,
    pps_out: 0,
    as_of: 0,
    trend: { bps_in: 0, bps_out: 0, pps_in: 0, pps_out: 0 },
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
        gap: 8,
        marginBottom: 12,
      }}
    >
      <Card
        label="BPS IN"
        value={d.bps_in}
        trendPct={d.trend.bps_in}
        format={fmtBps}
        accent="#4472C4"
      />
      <Card
        label="BPS OUT"
        value={d.bps_out}
        trendPct={d.trend.bps_out}
        format={fmtBps}
        accent="#ED7D31"
      />
      <Card
        label="PPS IN"
        value={d.pps_in}
        trendPct={d.trend.pps_in}
        format={fmtPps}
        accent="#70AD47"
      />
      <Card
        label="PPS OUT"
        value={d.pps_out}
        trendPct={d.trend.pps_out}
        format={fmtPps}
        accent="#FF4D4F"
      />
    </div>
  );
}
